import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  PULSE_INTERVAL_MS, fileUrl, fillPath, isFreeTemplateCode, isFreeVersion, isReportManager, useAttachments, useCollaboration, useEmailReport, useExports, useReport, useReportMutations,
  useReportPulse, useRequestCollaboration, useTemplateVersion, type ExportedFileDto,
} from '../api/hooks'
import type { ExportKind, FormInstanceDto, HeaderField, PhotoDto, Question } from '../api/types'
import { Badge, Empty, Modal, RevBadge, SecureImage, StatusBadge, Tabs, fmtAuditPeriod, fmtDate, fmtDateTime, gradeColor, severityColor } from '../components/ui'
import { useAuth } from '../auth/AuthProvider'
import { problemOf } from '../api/client'
import ExcelPreview from '../components/ExcelPreview'
import DownloadMenu from '../components/DownloadMenu'
import CollaborationPanel from '../components/CollaborationPanel'
import { ReportLogoControl } from '../components/LogoControl'
import { useArchiveMutations, useEntityHistory } from '../api/admin'
import { Changes } from './AuditLogPage'

export default function ReportDetailPage() {
  const { id = '' } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') ?? 'app'
  const report = useReport(id)
  const version = useTemplateVersion(report.data?.templateVersionId)
  const exportsQ = useExports(id)
  const attachments = useAttachments(id)
  const m = useReportMutations(id)
  const archive = useArchiveMutations()
  const history = useEntityHistory('FormInstance', tab === 'history' ? id : undefined)
  const [emailOpen, setEmailOpen] = useState(false)
  const [reviseOpen, setReviseOpen] = useState(false)
  // Live refresh: drafts change while people work on them; finished reports only need an occasional look (revise, approve, archive).
  const pulse = useReportPulse(id, !!report.data, report.data?.status === 'Draft' ? PULSE_INTERVAL_MS : 15_000)
  // Also asked when the report itself is hidden: somebody who only requested to join sees the state of that request.
  const { isError: reportFailed, refetch: refetchReport } = report
  const collab = useCollaboration(id, !!report.data || reportFailed, reportFailed ? 10_000 : false)
  const myStatus = collab.data?.myStatus
  // The request was approved while waiting on this page: the report is visible now.
  useEffect(() => {
    if (reportFailed && (myStatus === 'Approved' || myStatus === 'Owner')) void refetchReport()
  }, [reportFailed, myStatus, refetchReport])

  if (report.isLoading || (!report.data && collab.isLoading)) return <p>{t('common.loading')}</p>
  if (!report.data || pulse.lost) {
    return collab.data && (collab.data.myStatus === 'Pending' || collab.data.myStatus === 'Rejected')
      ? <JoinRequestNotice reportId={id} status={collab.data.myStatus} ownerName={collab.data.ownerName ?? undefined} />
      : <div className="card stack"><p className="error" style={{ margin: 0 }}>{pulse.lost ? t('collab.accessLost') : t('common.loadFailed')}</p><div><Link className="button secondary" to="/reports">{t('nav.reports')}</Link></div></div>
  }
  const r = report.data
  const canEdit = !!user?.roles.some((x) => ['SuperAdmin', 'TenantAdmin', 'Auditor'].includes(x.role))
  const isAdmin = user?.roles.some((x) => ['SuperAdmin', 'TenantAdmin'].includes(x.role))
  const isSuper = !!user?.roles.some((x) => x.role === 'SuperAdmin')
  // Deleting: the creator for drafts and completed reports, admins always (the server enforces the same).
  const canDelete = !!isAdmin || (!!user && r.ownerUserId === user.id && (r.status === 'Draft' || r.status === 'Completed'))
  const remove = () => {
    if (!confirm(t('reports.deleteConfirm'))) return
    m.remove.mutate(undefined, { onSuccess: () => navigate('/reports', { replace: true }) })
  }
  const forceClose = () => { if (confirm(t('reports.forceCloseConfirm'))) m.forceClose.mutate() }
  const isFree = isFreeTemplateCode(r.templateCode) || isFreeVersion(version.data)
  // Completing, revising, the logo and the participants belong to the report's owner (or a tenant admin); participants add content.
  const manager = canEdit && isReportManager(user, r)
  const editPath = isFree ? `/reports/${id}/free` : fillPath(r)
  const ownerName = collab.data?.ownerName
  const shared = r.collaboratorCount > 0

  return (
    <div className="stack">
      <div className="row between wrap">
        <div>
          <div className="row wrap" style={{ gap: 8 }}>
            <h2 style={{ margin: 0 }}>{r.code ?? r.templateCode}</h2>
            <RevBadge revisionNo={r.revisionNo} />
            <StatusBadge status={r.status} />
            <Badge>{r.templateCode}</Badge>
            <Badge>{t(`source.${r.source}`)}</Badge>
            {shared ? <Badge color="#1d6f42" title={t('collab.sharedHint')}>{t('collab.shared')}</Badge> : null}
          </div>
          <div className="muted">{r.title} · {r.companyName}{r.siteName ? ` · ${r.siteName}` : ''}</div>
        </div>
        <div className="row wrap">
          {r.status === 'Draft' && canEdit ? <Link className="button" to={editPath}>{t('collab.continueEditing')}</Link> : null}
          {r.status === 'Draft' && manager && !isFree ? <button onClick={() => { if (confirm(t('reports.completeConfirm'))) m.complete.mutate(false) }} disabled={m.complete.isPending}>{t('reports.complete')}</button> : null}
          {r.status === 'Completed' && manager ? <button className="secondary" onClick={() => { m.reopen.reset(); setReviseOpen(true) }}>{t('revise.action')}</button> : null}
          {r.status === 'Completed' && isAdmin ? <button onClick={() => m.approve.mutate()}>{t('reports.approve')}</button> : null}
          {(r.status === 'Completed' || r.status === 'Approved') && canEdit ? <button className="secondary" onClick={() => archive.archive.mutate(id)}>{t('archive.archive')}</button> : null}
          {r.status === 'Archived' && canEdit ? <button className="secondary" onClick={() => archive.unarchive.mutate(id)}>{t('archive.unarchive')}</button> : null}
          {r.status !== 'Draft' ? <button className="secondary" onClick={() => setEmailOpen(true)}>{t('emailReport.button')}</button> : null}
          {r.status === 'Draft' && isSuper ? <button className="secondary" onClick={forceClose} disabled={m.forceClose.isPending} title={t('reports.forceCloseConfirm')}>{t('reports.forceClose')}</button> : null}
          {canDelete ? <button className="secondary danger" onClick={remove} disabled={m.remove.isPending}>{t('reports.delete')}</button> : null}
        </div>
      </div>
      {m.remove.isError ? <p className="error small" role="alert" style={{ margin: 0 }}>{problemOf(m.remove.error).title ?? t('common.error')}</p> : null}
      {m.forceClose.isError ? <p className="error small" role="alert" style={{ margin: 0 }}>{problemOf(m.forceClose.error).title ?? t('common.error')}</p> : null}
      {r.status === 'Draft' && canEdit && !manager ? <p className="banner" role="note">{ownerName ? t('collab.participantBanner', { name: ownerName }) : t('collab.participantBannerNoName')}</p> : null}
      {r.status !== 'Draft' ? (
        <div className="download-area">
          <DownloadMenu reportId={id} />
          {manager && r.status !== 'Archived' ? <ReportLogoControl reportId={id} /> : null}
        </div>
      ) : null}
      <CollaborationPanel reportId={id} status={r.status} live={pulse.live && r.status === 'Draft'} />
      {emailOpen ? <EmailModal r={r} defaultTo={user?.email ?? ''} onClose={() => setEmailOpen(false)} /> : null}
      {reviseOpen ? (
        <Modal title={t('revise.title')} onClose={m.reopen.isPending ? () => undefined : () => setReviseOpen(false)} width={480}>
          <div className="stack">
            <p style={{ margin: 0 }}>{t('revise.explain')}</p>
            <ul className="small revise-points">
              <li>{t('revise.pointDraft')}</li>
              <li>{t('revise.pointNumber', { code: r.code ?? r.templateCode })}</li>
              <li>{t('revise.pointRev', { n: r.revisionNo + 1 })}</li>
            </ul>
            {m.reopen.isError ? <p className="error small" role="alert" style={{ margin: 0 }}>{problemOf(m.reopen.error).title ?? t('common.error')}</p> : null}
            <div className="row wrap" style={{ justifyContent: 'flex-end' }}>
              <button className="secondary" onClick={() => setReviseOpen(false)} disabled={m.reopen.isPending}>{t('common.cancel')}</button>
              <button onClick={() => m.reopen.mutate(undefined, { onSuccess: () => navigate(editPath) })} disabled={m.reopen.isPending}>{m.reopen.isPending ? t('common.pleaseWait') : t('revise.action')}</button>
            </div>
          </div>
        </Modal>
      ) : null}
      {m.complete.isError ? <p className="error">{(m.complete.error as { response?: { data?: { title?: string } } }).response?.data?.title ?? t('common.error')}</p> : null}

      <Tabs
        active={tab}
        onChange={(k) => setParams({ tab: k })}
        tabs={[
          { key: 'app', label: t('reports.tabApp') },
          { key: 'excel', label: 'Excel', badge: exportBadge(exportsQ.data, 'Excel') },
          { key: 'pdf', label: 'PDF', badge: exportBadge(exportsQ.data, 'Pdf') },
          ...(canEdit ? [{ key: 'history', label: t('auditLog.history') }] : []),
        ]}
      />

      {tab === 'app' ? <AppView r={r} free={isFree} headerFields={version.data?.headerFields ?? []} questions={version.data?.questions ?? []} attachments={attachments.data ?? []} /> : null}
      {tab === 'excel' ? <ExportView r={r} kind="Excel" files={exportsQ.data ?? []} onRequest={() => m.requestExport.mutate('excel')} requesting={m.requestExport.isPending} /> : null}
      {tab === 'pdf' ? <ExportView r={r} kind="Pdf" files={exportsQ.data ?? []} onRequest={() => m.requestExport.mutate('pdf')} requesting={m.requestExport.isPending} /> : null}
      {tab === 'history' ? (
        <div className="card">
          {history.isLoading ? <p>{t('common.loading')}</p> : !history.data?.length ? <Empty text={t('auditLog.empty')} /> : (
            <div className="table-wrap"><table className="table compact">
              <thead><tr><th>{t('auditLog.time')}</th><th>{t('auditLog.user')}</th><th>{t('auditLog.action')}</th><th>{t('auditLog.changes')}</th></tr></thead>
              <tbody>{history.data.map((l) => <tr key={l.id}><td className="small" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(l.timestamp)}</td><td className="small">{l.userEmail ?? t('auditLog.system')}</td><td><Badge>{t(`auditAction.${l.action}`)}</Badge></td><td className="small"><Changes entry={l} expanded /></td></tr>)}</tbody>
            </table></div>
          )}
        </div>
      ) : null}
    </div>
  )
}

/** Shown instead of the report to somebody whose join request is still waiting or was turned down (the report itself stays hidden). */
function JoinRequestNotice({ reportId, status, ownerName }: { reportId: string; status: 'Pending' | 'Rejected'; ownerName?: string }) {
  const { t } = useTranslation()
  const request = useRequestCollaboration()
  return (
    <div className="card stack" style={{ maxWidth: 560 }}>
      <h3 style={{ margin: 0 }}>{status === 'Pending' ? t('collab.waitingTitle') : t('collab.rejectedTitle')}</h3>
      <p className="muted" style={{ margin: 0 }}>{status === 'Pending' ? t('collab.waitingBody', { name: ownerName ?? t('collab.theOwner') }) : t('collab.rejectedBody')}</p>
      {request.isError ? <p className="error small" role="alert" style={{ margin: 0 }}>{problemOf(request.error).title ?? t('common.error')}</p> : null}
      <div className="row wrap">
        {status === 'Rejected' ? <button onClick={() => request.mutate(reportId)} disabled={request.isPending}>{t('collab.requestAgain')}</button> : null}
        <Link className="button secondary" to="/reports">{t('nav.reports')}</Link>
      </div>
    </div>
  )
}

/** "E-posta ile gönder": picks Word/Excel/PDF/PowerPoint, recipients (comma separated) and an optional file name; shows sentTo on success. */
function EmailModal({ r, defaultTo, onClose }: { r: FormInstanceDto; defaultTo: string; onClose: () => void }) {
  const { t } = useTranslation()
  const send = useEmailReport(r.id)
  const [picked, setPicked] = useState<Record<ExportKind, boolean>>({ Word: false, Excel: true, Pdf: true, PowerPoint: false })
  const [to, setTo] = useState(defaultTo)
  const [fileName, setFileName] = useState('')
  const dateSrc = r.auditStartDate ?? r.createdAt
  const [y, mo, d] = dateSrc.slice(0, 10).split('-')
  const autoName = `${r.title || r.code || r.templateName}_${d}-${mo}-${y}`
  const recipients = to.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean)
  const badRecipient = recipients.find((x) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x))
  const kinds = EMAIL_KINDS.filter((k) => picked[k.kind]).map((k) => k.kind)
  const canSend = kinds.length > 0 && recipients.length > 0 && !badRecipient && !send.isPending
  const problem = send.isError ? problemOf(send.error) : null

  return (
    <Modal title={t('emailReport.title')} onClose={onClose}>
      <div className="stack">
        <p className="muted small" style={{ margin: 0 }}>{t('emailReport.hint')}</p>
        <div>
          <div className="small" style={{ marginBottom: 4 }}>{t('emailReport.kinds')}</div>
          <div className="row wrap">
            {EMAIL_KINDS.map((k) => (
              <label key={k.kind} className="row"><input type="checkbox" checked={picked[k.kind]} onChange={(e) => setPicked((prev) => ({ ...prev, [k.kind]: e.target.checked }))} /> {k.label}</label>
            ))}
          </div>
          {kinds.length === 0 ? <p className="error small" style={{ margin: '4px 0 0' }}>{t('emailReport.pickKind')}</p> : null}
        </div>
        <label>{t('emailReport.recipients')}
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="ad@firma.com, ad2@firma.com" autoComplete="off" />
          <span className="muted small">{t('emailReport.recipientsHint')}</span>
          {badRecipient ? <span className="error small">{t('emailReport.invalidRecipient', { value: badRecipient })}</span> : null}
        </label>
        <label>{t('emailReport.fileName')}
          <input value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder={autoName} autoComplete="off" />
          <span className="muted small">{t('emailReport.fileNameHint', { name: autoName })}</span>
        </label>
        {send.isSuccess ? (
          <div className="card" style={{ background: 'var(--hover)' }}>
            <b>{t('emailReport.sent')}</b>
            <div className="small">{t('emailReport.sentTo')}: {send.data.sentTo.join(', ')}</div>
            {send.data.attachments.length > 0 ? <ul className="small" style={{ margin: '4px 0 0' }}>{send.data.attachments.map((a) => <li key={a.fileName}>{a.fileName} <span className="muted">({(a.sizeBytes / 1024).toFixed(0)} KB)</span></li>)}</ul> : null}
          </div>
        ) : null}
        {problem ? <p className="error small" style={{ margin: 0 }}>{problem.title ?? t('emailReport.failed')}{problem.detail ? <><br /><span className="muted">{problem.detail}</span></> : null}</p> : null}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="secondary" onClick={onClose}>{send.isSuccess ? t('emailReport.close') : t('common.cancel')}</button>
          <button disabled={!canSend} onClick={() => send.mutate({ kinds, to: recipients, fileName: fileName.trim() || null })}>{send.isPending ? t('emailReport.sending') : t('emailReport.send')}</button>
        </div>
      </div>
    </Modal>
  )
}

const EMAIL_KINDS: { kind: ExportKind; label: string }[] = [{ kind: 'Word', label: 'Word' }, { kind: 'Excel', label: 'Excel' }, { kind: 'Pdf', label: 'PDF' }, { kind: 'PowerPoint', label: 'PowerPoint' }]

function exportBadge(files: ExportedFileDto[] | undefined, kind: 'Excel' | 'Pdf') {
  const current = files?.find((f) => f.kind === kind && f.isCurrent)
  if (!current) return null
  const color = current.status === 'Ready' ? (current.isStale ? '#eda100' : '#008300') : current.status === 'Failed' ? '#e34948' : '#2a78d6'
  return <span className="dot" style={{ background: color }} />
}

type AttachmentLite = { id: string; kind: string; signerName?: string; fileId: string; signedAt?: string }

function headerText(v: unknown): string {
  const one = (x: unknown) => (/^\d{4}-\d{2}-\d{2}$/.test(String(x)) ? fmtDate(String(x)) : String(x))
  if (Array.isArray(v)) return v.filter((x) => x !== '' && x != null).map(one).join(' – ')
  if (v == null || v === '') return ''
  return typeof v === 'object' ? JSON.stringify(v) : one(v)
}

/** Free-form report ("Boş Rapor"): numbered items, each a note plus its photos. No questions, scores, severities or actions. */
function FreeItemsView({ r, headerFields, attachments }: { r: FormInstanceDto; headerFields: HeaderField[]; attachments: AttachmentLite[] }) {
  const { t } = useTranslation()
  const [lightbox, setLightbox] = useState<string | null>(null)
  const items = useMemo(() => r.findings.filter((f) => !f.questionId).sort((a, b) => a.sortOrder - b.sortOrder), [r.findings])
  const photosOf = useMemo(() => {
    const map = new Map<string, PhotoDto[]>()
    for (const p of [...r.photos].sort((a, b) => a.sortOrder - b.sortOrder)) {
      const key = p.findingId ?? 'other'
      map.set(key, [...(map.get(key) ?? []), p])
    }
    return map
  }, [r.photos])
  const itemIds = new Set(items.map((x) => x.id))
  const loose = r.photos.filter((p) => !p.actionId && (!p.findingId || !itemIds.has(p.findingId))).sort((a, b) => a.sortOrder - b.sortOrder)
  const signatures = attachments.filter((a) => a.kind.endsWith('Signature'))
  const labels = new Map(headerFields.map((f) => [f.key, f.label]))
  const header = Object.entries(r.headerValues)
    .filter(([k]) => headerFields.find((f) => f.key === k)?.type !== 'Company')
    .map(([k, v]) => [k, headerText(v)] as const)
    .filter(([, text]) => text !== '')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setLightbox(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="stack">
      <section className="card">
        <h3>{t('reports.header')}</h3>
        <dl className="dl">
          {header.map(([k, text]) => <div key={k}><dt>{labels.get(k) ?? k}</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{text}</dd></div>)}
          <div><dt>{t('freeReport.preparedBy')}</dt><dd>{r.auditors.map((a) => a.displayName).join(' / ') || '–'}</dd></div>
        </dl>
        {r.notes ? <p style={{ whiteSpace: 'pre-wrap' }}>{r.notes}</p> : null}
      </section>

      <section className="card">
        <h3>{t('freeReport.items')} ({items.length})</h3>
        {items.length === 0 ? <Empty text={t('freeReport.noItems')} /> : (
          <div className="free-items">
            {items.map((item, i) => {
              const photos = photosOf.get(item.id) ?? []
              return (
                <article key={item.id} className="free-item readonly">
                  <span className="free-no" aria-label={`${t('reports.item')} ${item.number ?? i + 1}`}>{item.number ?? i + 1}</span>
                  <div className="free-item-body">
                    {item.description.trim() ? <p className="free-note">{item.description}</p> : <p className="muted free-note">{t('freeReport.noNote')}</p>}
                    {photos.length > 0 ? (
                      <div className="thumbs">
                        {photos.map((p, j) => (
                          <figure key={p.id}>
                            <SecureImage fileId={p.thumbnailFileId ?? p.fileId} alt={p.caption ?? ''} className="thumb" onClick={() => setLightbox(p.fileId)} />
                            <figcaption className="muted small">{j + 1}/{photos.length} · {fmtDateTime(p.capturedAt)}</figcaption>
                          </figure>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </section>

      {loose.length > 0 ? (
        <section className="card">
          <h3>{t('reports.photos')} · {t('reports.other')} ({loose.length})</h3>
          <div className="thumbs">
            {loose.map((p, j) => (
              <figure key={p.id}>
                <SecureImage fileId={p.thumbnailFileId ?? p.fileId} alt={p.caption ?? ''} className="thumb" onClick={() => setLightbox(p.fileId)} />
                <figcaption className="muted small">{j + 1}/{loose.length} · {fmtDateTime(p.capturedAt)}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      ) : null}

      {signatures.length > 0 ? (
        <section className="card">
          <h3>{t('reports.signatures')}</h3>
          {signatures.map((a) => (
            <div key={a.id} className="signature">
              <SecureImage fileId={a.fileId} alt={a.kind} style={{ maxHeight: 80 }} />
              <div className="small">{t(`attachment.${a.kind}`)}{a.signerName ? ` – ${a.signerName}` : ''} <span className="muted">{fmtDateTime(a.signedAt)}</span></div>
            </div>
          ))}
        </section>
      ) : null}
      {lightbox ? <div className="modal-backdrop" onClick={() => setLightbox(null)}><SecureImage fileId={lightbox} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} /></div> : null}
    </div>
  )
}

function AppView(props: { r: FormInstanceDto; free: boolean; headerFields: HeaderField[]; questions: Question[]; attachments: AttachmentLite[] }) {
  return props.free ? <FreeItemsView r={props.r} headerFields={props.headerFields} attachments={props.attachments} /> : <QuestionsView r={props.r} questions={props.questions} attachments={props.attachments} />
}

function QuestionsView({ r, questions, attachments }: { r: FormInstanceDto; questions: Question[]; attachments: AttachmentLite[] }) {
  const { t } = useTranslation()
  const byId = useMemo(() => new Map(questions.map((q) => [q.id, q])), [questions])
  const answersByQ = useMemo(() => new Map(r.answers.map((a) => [a.questionId, a])), [r.answers])
  const [lightbox, setLightbox] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setLightbox(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const photosByQuestion = useMemo(() => {
    const map = new Map<string, typeof r.photos>()
    for (const p of [...r.photos].sort((a, b) => (a.questionNo ?? 0) - (b.questionNo ?? 0) || a.sortOrder - b.sortOrder)) {
      const key = p.questionId ?? 'other'
      map.set(key, [...(map.get(key) ?? []), p])
    }
    return map
  }, [r.photos])

  return (
    <div className="grid detail">
      <div className="stack">
        <section className="card">
          <h3>{t('reports.header')}</h3>
          <dl className="dl">
            {Object.entries(r.headerValues).map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd>{Array.isArray(v) ? v.filter((x) => x !== '' && x != null).map((x) => (/^\d{4}-\d{2}-\d{2}$/.test(String(x)) ? fmtDate(String(x)) : String(x))).join(' – ') : typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? fmtDate(v) : String(v ?? '')}</dd></div>
            ))}
            <div><dt>{t('reports.auditors')}</dt><dd>{r.auditors.map((a) => a.displayName).join(' / ')}</dd></div>
            <div><dt>{t('reports.date')}</dt><dd>{fmtAuditPeriod(r.auditStartDate, r.auditStartTime, r.auditEndDate, r.auditEndTime)}</dd></div>
          </dl>
          {r.notes ? <p style={{ whiteSpace: 'pre-wrap' }}>{r.notes}</p> : null}
        </section>

        {r.score ? (
          <section className="card">
            <div className="row between">
              <h3>{t('reports.scoreTable')}</h3>
              <span className="hero" style={{ color: gradeColor[r.score.grade] }}>%{r.score.percent} {r.score.grade}</span>
            </div>
            <div className="table-wrap">
              <table className="table compact">
                <thead><tr><th>#</th><th>{t('dashboard.group')}</th><th className="num">{t('score.total')}</th><th className="num">{t('score.earned')}</th><th className="num">%</th><th>{t('score.grade')}</th><th className="num">{t('score.major')}</th><th className="num">{t('score.zero')}</th></tr></thead>
                <tbody>
                  {r.score.groups.map((g) => (
                    <tr key={g.number}>
                      <td>{g.number}</td><td>{g.name}</td><td className="num">{g.totalPossible}</td><td className="num">{g.totalEarned.toFixed(2)}</td><td className="num">{g.percent}%</td>
                      <td><Badge color={gradeColor[g.grade]}>{g.grade}</Badge></td><td className="num">{g.majorCount}</td><td className="num">{g.zeroToleranceCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        <section className="card">
          <h3>{t('reports.findings')} ({r.findings.length})</h3>
          {r.findings.length === 0 ? <Empty text={t('common.none')} /> : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>NO</th><th>{t('finding.group')}</th><th>{t('finding.severity')}</th><th>{t('finding.description')}</th><th>{t('finding.action')}</th><th>{t('finding.responsible')}</th><th>{t('finding.dueDate')}</th><th>{t('finding.actionNo')}</th></tr></thead>
                <tbody>
                  {r.findings.map((f) => {
                    const q = f.questionId ? byId.get(f.questionId) : undefined
                    const a = f.questionId ? answersByQ.get(f.questionId) : undefined
                    return (
                      <tr key={f.id}>
                        <td>{f.number ?? '–'}</td>
                        <td className="small">{q ? `S.${q.questionNo}` : ''}</td>
                        <td><Badge color={severityColor[f.severity]}>{t(`severity.${f.severity}`)}</Badge></td>
                        <td className="small">
                          {q ? <div><b>{t('finding.question')}:</b> {q.text}</div> : null}
                          {a?.selectedOptionCode ? <div><b>{t('finding.selected')}:</b> {a.selectedOptionCode}</div> : null}
                          <div><b>{t('finding.explanation')}:</b> {f.description}</div>
                          {f.photos.length > 0 ? <div className="thumbs">{f.photos.map((p) => <SecureImage key={p.id} fileId={p.thumbnailFileId ?? p.fileId} alt="" className="thumb" onClick={() => setLightbox(p.fileId)} />)}</div> : null}
                        </td>
                        <td className="small" style={{ whiteSpace: 'pre-wrap' }}>{f.actionText}</td>
                        <td className="small">{f.responsibleName}</td>
                        <td className="small">{fmtDate(f.dueDate)}</td>
                        <td className="small">{f.actionNo ?? '–'}{f.actionStatus ? <div className="muted">{t(`actionStatus.${f.actionStatus}`)}</div> : null}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="card">
          <h3>{t('reports.answers')} ({r.answers.filter((a) => a.selectedOptionCode).length}/{questions.length})</h3>
          <div className="table-wrap">
            <table className="table compact">
              <thead><tr><th>S.NO</th><th>{t('reports.question')}</th><th>{t('reports.answer')}</th><th>{t('reports.comment')}</th></tr></thead>
              <tbody>
                {questions.map((q) => {
                  const a = answersByQ.get(q.id)
                  return (
                    <tr key={q.id}>
                      <td>{q.questionNo}</td>
                      <td className="small">{q.text}</td>
                      <td>{a?.selectedOptionCode ? <Badge>{a.selectedOptionCode}</Badge> : <span className="muted">–</span>}{a?.earnedPoints != null && q.maxPoints > 0 ? <div className="muted small">{a.earnedPoints}/{q.maxPoints}</div> : null}</td>
                      <td className="small">{a?.comment}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <aside className="stack">
        <section className="card">
          <h3>{t('reports.photos')} ({r.photos.length})</h3>
          {r.photos.length === 0 ? <Empty text={t('common.none')} /> : null}
          {[...photosByQuestion.entries()].map(([qid, photos]) => {
            const q = byId.get(qid)
            return (
              <div key={qid} className="photo-group">
                <div className="small"><b>{q ? `${t('reports.item')} ${q.questionNo}` : t('reports.other')}</b>{q ? <span className="muted"> – {q.text.slice(0, 80)}{q.text.length > 80 ? '…' : ''}</span> : null}</div>
                <div className="thumbs">
                  {photos.map((p, i) => (
                    <figure key={p.id}>
                      <SecureImage fileId={p.thumbnailFileId ?? p.fileId} alt="" className="thumb" onClick={() => setLightbox(p.fileId)} />
                      <figcaption className="muted small">{i + 1}/{photos.length} · {fmtDateTime(p.capturedAt)}</figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            )
          })}
        </section>
        <section className="card">
          <h3>{t('reports.signatures')}</h3>
          {attachments.filter((a) => a.kind.endsWith('Signature')).length === 0 ? <Empty text={t('common.none')} /> : null}
          {attachments.filter((a) => a.kind.endsWith('Signature')).map((a) => (
            <div key={a.id} className="signature">
              <SecureImage fileId={a.fileId} alt={a.kind} style={{ maxHeight: 80 }} />
              <div className="small">{t(`attachment.${a.kind}`)}{a.signerName ? ` – ${a.signerName}` : ''} <span className="muted">{fmtDateTime(a.signedAt)}</span></div>
            </div>
          ))}
        </section>
      </aside>
      {lightbox ? <div className="modal-backdrop" onClick={() => setLightbox(null)}><SecureImage fileId={lightbox} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} /></div> : null}
    </div>
  )
}

function ExportView({ r, kind, files, onRequest, requesting }: { r: FormInstanceDto; kind: 'Excel' | 'Pdf'; files: ExportedFileDto[]; onRequest: () => void; requesting: boolean }) {
  const { t } = useTranslation()
  const current = files.find((f) => f.kind === kind && f.isCurrent) ?? files.find((f) => f.kind === kind)
  const [url, setUrl] = useState<string>()
  useEffect(() => {
    setUrl(undefined)
    if (current?.fileId && current.status === 'Ready') void fileUrl(current.fileId).then(setUrl)
  }, [current?.fileId, current?.status])

  return (
    <section className="card stack">
      <div className="row between wrap">
        <div>
          {current ? (
            <>
              <Badge color={current.status === 'Ready' ? '#008300' : current.status === 'Failed' ? '#e34948' : '#2a78d6'}>{t(`exportStatus.${current.status}`)}</Badge>
              {current.isStale ? <Badge color="#eda100">{t('exports.stale')}</Badge> : null}
              <span className="muted small"> v{current.version} · {current.fileName} · {fmtDateTime(current.generatedAt)}</span>
              {current.error ? <div className="error small">{current.error}</div> : null}
            </>
          ) : <span className="muted">{t('exports.none')}</span>}
        </div>
        <div className="row">
          <button className="secondary" onClick={onRequest} disabled={requesting || r.status === 'Draft'}>{t('exports.generate')}</button>
          {url ? <a className="button" href={url} download={current?.fileName}>{t('exports.download')}</a> : null}
        </div>
      </div>
      {r.status === 'Draft' ? <p className="muted small">{t('exports.draftHint')}</p> : null}
      {url && kind === 'Pdf' ? <iframe title="pdf" src={url} className="preview" /> : null}
      {url && kind === 'Excel' ? <ExcelPreview url={url} /> : null}
      {files.filter((f) => f.kind === kind).length > 1 ? (
        <details>
          <summary className="muted small">{t('exports.history')}</summary>
          <ul className="small">{files.filter((f) => f.kind === kind).map((f) => <li key={f.id}>v{f.version} · {t(`exportStatus.${f.status}`)} · {fmtDateTime(f.generatedAt)}</li>)}</ul>
        </details>
      ) : null}
    </section>
  )
}
